<template>
    <page :tabs="tabs" :busy="busy">
        <grid ref="grid" :isAutoHeight="true" :columnDefs="columnDefs" :rowData="rowData" :isAutoWidth="true">
            <responsive-button variant="primary" :icon="['fas', 'plus']" @click="addForm">Add Form</responsive-button>
        </grid>
    </page>
</template>

<script>
import api from '../../../api';
import page from '../../generic/page/page.vue';
import { HOSTED_FORM_ENDPOINT } from '../../../endpoints';
import { formatResponseError } from '../../../utils/api';
import ActionCellRenderer from './action-cell-renderer.vue';
import grid from '../../generic/grid/grid.vue';
import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
import { escapeHTML } from '../../../utils/strings';
import DisabledActionsCellRenderer from './disabled-actions-cell-renderer.vue';
import { valueFormatter } from '../../../utils/billing';

export default {
    components: {
        grid,
        ResponsiveButton,
        page,
        actionCellRenderer: ActionCellRenderer,
        disabledActionsCellRenderer: DisabledActionsCellRenderer,
    },
    data() {
        return {
            rowData: [],
            busy: false,
        };
    },
    computed: {
        tabs() {
            return [
                {
                    title: 'Hosted Form List',
                    route: '/app/hosted-form/list/',
                    active: this.$route.path === '/app/hosted-form/list/',
                },
                {
                    title: 'Requested Form',
                    route: '/app/hosted-form/pending/',
                    active: this.$route.path === '/app/hosted-form/pending/',
                },
                {
                    title: 'Сompleted Forms',
                    route: '/app/hosted-form/completed/',
                    active: this.$route.path === '/app/hosted-form/completed/',
                },
            ];
        },
        columnDefs() {
            return [
                {
                    field: 'id',
                    headerName: '#',
                    maxWidth: 120,
                },
                {
                    field: 'title',
                    headerName: 'Title',
                },
                {
                    field: 'properties',
                    headerName: 'Properties',
                    cellRenderer: ({ data }) =>
                        data.properties.length
                            ? data.properties.map(({ name }) => `${escapeHTML(name)}`).join('<br/>')
                            : 'Default',
                    autoHeight: true,
                    valueFormatter: ({ data }) => data.properties,
                    autoWidthExclude: true
                },
                {
                    field: 'variable.name',
                    headerName: 'Merge Tag',
                    cellRenderer: ({ data }) => data.variable?.name || '-',
                    valueFormatter: ({ data }) => data.variable?.name || '-',
                },
                {
                    field: 'variable.title',
                    headerName: 'Merge Tag Description',
                    cellRenderer: ({ data }) => data.variable?.title || '-',
                    valueFormatter: ({ data }) => data.variable?.title || '-',
                },
                {
                    field: 'type_label',
                    headerName: 'Type',
                },
                {
                    headerName: 'Enabled',
                    cellRenderer: 'disabledActionsCellRenderer',
                    maxWidth: 120,
                    minWidth: 120,
                    cellRendererParams: {
                        onChange: async (id) => {
                            try {
                                this.busy = true;
                                await api.post(HOSTED_FORM_ENDPOINT(id) + 'toggle-enabled/');
                            } catch (e) {
                                this.$refs.grid.gridOptions.api.redrawRows();
                                const { generic } = formatResponseError(e);
                                this.errorToast({ text: generic || 'Could not update hosted form' });
                            } finally {
                                this.busy = false;
                            }
                        },
                    },
                },
                {
                    field: '',
                    cellRenderer: 'actionCellRenderer',
                    maxWidth: 180,
                    minWidth: 180,
                    cellRendererParams: {
                        delete: async (id) => {
                            try {
                                this.busy = true;
                                await api.delete(HOSTED_FORM_ENDPOINT(id));
                                await this.fetchData();
                            } catch (e) {
                                const { generic } = formatResponseError(e);
                                this.errorToast({ text: generic || 'Could not delete hosted form' });
                            } finally {
                                this.busy = false;
                            }
                        },
                        clone: async (id) => {
                            try {
                                this.busy = true;
                                await api.post(HOSTED_FORM_ENDPOINT(id) + 'clone/');
                                await this.fetchData();
                            } catch (e) {
                                const { generic } = formatResponseError(e);
                                this.errorToast({ text: generic || 'Could not clone hosted form' });
                            } finally {
                                this.busy = false;
                            }
                        },
                        reset: async (id) => {
                            try {
                                this.busy = true;
                                await api.post(HOSTED_FORM_ENDPOINT(id) + 'reset/');
                                await this.fetchData();
                                this.successToast({ text: 'Checklist reset successfully' });
                            } catch (e) {
                                const { generic } = formatResponseError(e);
                                this.errorToast({ text: generic || 'Could not reset hosted form' });
                            } finally {
                                this.busy = false;
                            }
                        },
                    },
                },
            ];
        },
    },
    mounted() {
        this.fetchData();
    },
    methods: {
        async fetchData() {
            try {
                this.busy = true;
                const { data } = await api.get(HOSTED_FORM_ENDPOINT());
                this.rowData = data;
            } catch (error) {
                const { generic } = formatResponseError(error);
                this.errorToast({ text: generic || 'Could not fetch hosted forms' });
            } finally {
                this.busy = false;
            }
        },
        addForm() {
            this.$router.push('/app/hosted-form/0/edit/');
        },
    },
};
</script>

<style></style>
